import { ReactNode } from 'react';
import { ToastOptions } from 'react-toastify';

import formatDecimal from '@paradigm/utils/formatDecimal';
import { neonLemon } from '@paradigm/design-system/src/colors';

import checkIcon from '@paradigm/design-system/src/assets/icon-check.svg';

import toast from '#/components/toasts/toast';
import { Maybe } from '@paradigm/utils/types';
import { getProductName } from '@paradigm/features/src/products/domain';
import { getProductCodesDescription } from '@paradigm/features/src/unified-rfqs/utils/productCodes';
import { TOAST_TIMEOUT_LONG } from '../UnifiedRfqs/toast';
import {
  Container,
  Description,
  DescriptionContainer,
  Price,
  PriceContainer,
  PriceTitle,
  Quantity,
  Title,
} from './components';
import LegacyToastIcon from './LegacyToastIcon';

/** action and side are the same thing, both parameters were added to conform with different apis */
export interface ToastableTrade {
  readonly action?: 'BUY' | 'SELL' | undefined;
  readonly side?: 'BUY' | 'SELL' | undefined;
  readonly description: ReactNode | string;
  readonly productCode?: Maybe<string>;
  /** More than one entry means a multi-asset structure — the title shows the joined codes instead of one product's name. */
  readonly productCodes?: readonly string[];
  readonly quantity: ReactNode | string;
  readonly price: ReactNode | string;
}

export function toastTrade(
  trade: ToastableTrade,
  options: Partial<ToastOptions> = {},
) {
  toast(getContent(trade), {
    icon: <LegacyToastIcon src={checkIcon} />,
    position: 'top-right',
    styles: {
      backgroundColor: neonLemon,
      borderColor: neonLemon,
      color: 'white',
    },
    autoClose: TOAST_TIMEOUT_LONG,
    ...options,
  });
}

function formatQuantity(quantity: ReactNode | string) {
  if (typeof quantity === 'string') {
    return formatDecimal(quantity);
  }
  return quantity;
}

function formatPrice(price: ReactNode | string) {
  if (typeof price === 'string') {
    return formatDecimal(price);
  }
  return price;
}

function getContent(trade: ToastableTrade) {
  const { action, description, side, productCode, productCodes } = trade;
  const quantity = formatQuantity(trade.quantity);
  const price = formatPrice(trade.price);
  const productName =
    productCodes != null && productCodes.length > 1
      ? getProductCodesDescription(productCodes)
      : productCode != null
      ? getProductName(productCode)
      : '';

  return (
    <Container data-testid="trade-info-toast">
      <Title aria-label="toast trade action">
        {getActionText(action ?? side)} {productName}
      </Title>
      <DescriptionContainer>
        <Description aria-label="toast trade description">
          {description}
        </Description>
        <Quantity aria-label="toast trade quantity">{quantity}x</Quantity>
      </DescriptionContainer>
      <PriceContainer>
        <PriceTitle>at</PriceTitle>
        <Price aria-label="toast trade price">{price}</Price>
      </PriceContainer>
    </Container>
  );
}

function getActionText(action: 'BUY' | 'SELL' | undefined) {
  switch (action) {
    case 'BUY':
      return 'Bought';
    case 'SELL':
      return 'Sold';
    default:
      return 'Executed';
  }
}
